import { type BackendSavedFilter, UNIVERSAL_FILTER_ALL_ID } from './types';
import {
  type UniversalFiltersStore,
  type UniversalSavedFilterUrlSync,
} from './UniversalFiltersProvider.types';

type SavedFilterSelectionSyncInput = {
  /** Prevents applying route ids before the saved-filters query has settled. */
  readonly isSavedFiltersLoading: boolean;
  /**
   * Last selected id observed by the provider effect.
   *
   * A changed value means the store changed locally, so the route should follow
   * the store instead of treating the route as the source of truth.
   */
  readonly previousSelectedFilterIdRef: {
    current: string;
  };
  /** Saved-filter id currently encoded in the route search params. */
  readonly routeSavedFilterId?: string;
  /** Saved filters available from the page-owned query. */
  readonly savedFilters: readonly BackendSavedFilter[];
  /** Page-owned route adapter used to write savedFilterId changes. */
  readonly savedFilterUrlSync: UniversalSavedFilterUrlSync;
  /** Saved-filter id currently selected in the universal filters store. */
  readonly selectedFilterId: string;
  /** Provider-scoped universal filters store. */
  readonly store: UniversalFiltersStore;
};

/**
 * Checks whether provider effects should run saved-filter route sync.
 *
 * Saved-filter URL sync only makes sense when the page enabled saved filters
 * and supplied a route adapter. The type predicate lets the provider call the
 * sync function without repeating undefined checks.
 */
export function shouldSyncSavedFilterSelection(
  hasSavedFiltersEnabled: boolean,
  savedFilterUrlSync?: UniversalSavedFilterUrlSync,
): savedFilterUrlSync is UniversalSavedFilterUrlSync {
  return hasSavedFiltersEnabled && savedFilterUrlSync !== undefined;
}

/**
 * Coordinates the two sync directions while giving local selection changes priority.
 *
 * The route is the source of truth on initial load and browser navigation, but
 * local store changes should win immediately after a user selects, clears, or
 * creates a saved filter. `previousSelectedFilterIdRef` is what distinguishes
 * those two cases inside the provider effect.
 */
export function syncSavedFilterSelection({
  isSavedFiltersLoading,
  previousSelectedFilterIdRef,
  routeSavedFilterId,
  savedFilters,
  savedFilterUrlSync,
  selectedFilterId,
  store,
}: SavedFilterSelectionSyncInput) {
  const hasSelectedFilterIdChanged =
    previousSelectedFilterIdRef.current !== selectedFilterId;
  previousSelectedFilterIdRef.current = selectedFilterId;

  if (hasSelectedFilterIdChanged) {
    syncSelectedFilterToRoute({
      routeSavedFilterId,
      savedFilterUrlSync,
      selectedFilterId,
    });
    return;
  }

  if (isSavedFiltersLoading) return;

  syncRouteToSelectedFilter({
    routeSavedFilterId,
    savedFilters,
    savedFilterUrlSync,
    selectedFilterId,
    store,
  });
}

/**
 * Writes user-driven saved-filter selections into the route.
 *
 * Store selections use the provider's "All" sentinel, while the route represents
 * "All" as an absent savedFilterId. Selection changes reset pagination because
 * the visible result set has changed.
 */
function syncSelectedFilterToRoute({
  routeSavedFilterId,
  savedFilterUrlSync,
  selectedFilterId,
}: Pick<
  SavedFilterSelectionSyncInput,
  'routeSavedFilterId' | 'savedFilterUrlSync' | 'selectedFilterId'
>) {
  const nextRouteSavedFilterId = getRouteSavedFilterId(selectedFilterId);

  if (routeSavedFilterId === nextRouteSavedFilterId) return;

  void savedFilterUrlSync.writeSavedFilterId({
    resetPage: true,
    savedFilterId: nextRouteSavedFilterId,
  });
}

/**
 * Applies the route savedFilterId to the store once saved filters are available.
 *
 * Known ids select their backend baseline, absent ids select the built-in "All"
 * filter, and unknown ids are treated as stale route state that should be
 * cleaned up.
 */
function syncRouteToSelectedFilter({
  routeSavedFilterId,
  savedFilters,
  savedFilterUrlSync,
  selectedFilterId,
  store,
}: Pick<
  SavedFilterSelectionSyncInput,
  | 'routeSavedFilterId'
  | 'savedFilters'
  | 'savedFilterUrlSync'
  | 'selectedFilterId'
  | 'store'
>) {
  if (!routeSavedFilterId) {
    selectAllFilterIfNeeded({ selectedFilterId, store });
    return;
  }

  const matchingSavedFilter = savedFilters.find(
    (filter) => filter.id === routeSavedFilterId,
  );

  if (!matchingSavedFilter) {
    clearUnknownRouteSavedFilterId({
      routeSavedFilterId,
      savedFilterUrlSync,
      selectedFilterId,
    });
    return;
  }

  selectRouteSavedFilterIfNeeded({
    matchingSavedFilter,
    routeSavedFilterId,
    selectedFilterId,
    store,
  });
}

/**
 * Selects the synthetic "All" filter when the route does not name a saved filter.
 */
function selectAllFilterIfNeeded({
  selectedFilterId,
  store,
}: Pick<SavedFilterSelectionSyncInput, 'selectedFilterId' | 'store'>) {
  if (selectedFilterId === UNIVERSAL_FILTER_ALL_ID) return;

  store.trigger.selectFilter({
    baseline: [],
    filterId: UNIVERSAL_FILTER_ALL_ID,
  });
}

/**
 * Removes a stale route savedFilterId without resetting pagination.
 *
 * If the unknown id is also the currently selected local id, the saved-filter
 * query is probably catching up after create. In that case the route id is kept
 * so the just-created filter does not disappear from the URL.
 */
function clearUnknownRouteSavedFilterId({
  routeSavedFilterId,
  savedFilterUrlSync,
  selectedFilterId,
}: Pick<
  SavedFilterSelectionSyncInput,
  'routeSavedFilterId' | 'savedFilterUrlSync' | 'selectedFilterId'
>) {
  if (
    selectedFilterId !== UNIVERSAL_FILTER_ALL_ID &&
    routeSavedFilterId === selectedFilterId
  ) {
    return;
  }

  void savedFilterUrlSync.writeSavedFilterId({
    resetPage: false,
    savedFilterId: undefined,
  });
}

/**
 * Selects the saved filter referenced by the route when it differs from the store.
 */
function selectRouteSavedFilterIfNeeded({
  matchingSavedFilter,
  routeSavedFilterId,
  selectedFilterId,
  store,
}: Pick<
  SavedFilterSelectionSyncInput,
  'routeSavedFilterId' | 'selectedFilterId' | 'store'
> & {
  readonly matchingSavedFilter: BackendSavedFilter;
}) {
  if (selectedFilterId === routeSavedFilterId) return;

  store.trigger.selectFilter({
    baseline: matchingSavedFilter.filters,
    filterId: matchingSavedFilter.id,
  });
}

/**
 * Converts the store's "All" sentinel into the route's empty savedFilterId state.
 */
function getRouteSavedFilterId(selectedFilterId: string) {
  return selectedFilterId === UNIVERSAL_FILTER_ALL_ID
    ? undefined
    : selectedFilterId;
}
